import React, { useEffect, useMemo, useState } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiHeart,
} from "react-icons/fi";

const STORAGE_KEY = "public_promo_panel_open_v1";

export default function PublicPromoPanel({
  photoUrl = "/img/laurent_victorin.webp",
  name = "Laurent VICTORIN",
  title = "Président de Club • Enseignant Judo • Membre de Comité Départemental",
  subtitle = "Développeur d'Applications Web & Sites Internet",
  siteUrl = "https://www.monappliclub.com/",
  donationUrl = "https://buy.stripe.com/cN2aFtaqL5fC1AkcMR",
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === null)
        setOpen(false); // par défaut fermé
      else setOpen(raw === "1");
    } catch {
      setOpen(false);
    }
  }, []);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  };

  const apps = useMemo(
    () => [
      "MonAppliTournoi",
      "MonAppliClub",
      "MonAppliComité",
      "MonAppliLigue",
      "Judo Score Pro",
      "Judo Présence Pro",
      "Judo Coach Pro",
      "Randori Cards",
      "MonAppliMairie",
      "MonAppliAsso",
      "MonAppliKata",
      "Sites web sur-mesure",
    ],
    [],
  );

  return (
    <div className="mx-auto max-w-[1280px] w-full mb-5">
      <div className="rounded-3xl overflow-hidden border border-gray-200/70 dark:border-slate-700/70 shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
        {/* Bandeau */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-indigo-700 to-slate-900" />
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.9),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.5),transparent_40%),radial-gradient(circle_at_60%_90%,rgba(255,255,255,0.35),transparent_45%)]" />
          <div className="relative px-4 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2">
                <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/80">
                  Soutien au développement des applications Judo
                </span>
                <span className="hidden sm:inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-white/15 text-white ring-1 ring-white/20">
                  Don 5€ • Déduction fiscale
                </span>
              </div>

              <div className="mt-1 text-lg sm:text-xl font-semibold text-white truncate">
                {name}
              </div>
              <div className="mt-0.5 text-xs sm:text-sm text-white/85 leading-snug">
                {title} | {subtitle}
              </div>
            </div>

            <button
              type="button"
              onClick={toggle}
              className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 text-white px-3 py-2 text-sm font-semibold ring-1 ring-white/20 transition"
              aria-expanded={open ? "true" : "false"}
            >
              {open ? (
                <>
                  <FiChevronUp /> Fermer
                </>
              ) : (
                <>
                  <FiChevronDown /> Ouvrir
                </>
              )}
            </button>
          </div>
        </div>

        {/* Contenu */}
        {open && (
          <div className="bg-white/80 dark:bg-slate-900/70 backdrop-blur px-4 sm:px-6 py-4 sm:py-5">
            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 sm:gap-5">
              {/* Carte identité */}
              <div className="rounded-3xl border border-gray-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-900/40 p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl overflow-hidden bg-gray-100 dark:bg-slate-800 ring-1 ring-gray-200/70 dark:ring-slate-700/70 shrink-0">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-300">
                        Photo
                      </div>
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate">
                      {name}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-snug">
                      {title}
                      <br />
                      {subtitle}
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="text-sm text-gray-800 dark:text-gray-200">
                    Je crée et maintiens des outils modernes pour faciliter
                    l’organisation des clubs et compétitions de Judo.
                  </div>

                  <div className="rounded-2xl bg-blue-50/70 dark:bg-blue-900/20 border border-blue-100/70 dark:border-blue-900/30 p-3">
                    <div className="text-sm font-bold text-blue-900 dark:text-blue-100">
                      Objectif : développer plus d’apps Judo
                    </div>
                    <div className="text-xs text-blue-900/80 dark:text-blue-100/80 mt-1">
                      Votre soutien (don 5€) aide directement à financer le
                      temps de développement et les améliorations.
                    </div>
                  </div>
                </div>
              </div>

              {/* Carte contenu */}
              <div className="rounded-3xl border border-gray-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-900/40 p-4 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                      Découvrir mes projets & soutenir le développement
                    </div>
                    <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      Retrouvez toutes mes applications Judo et mes services de
                      création de sites / outils sur mesure.
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <a
                      href={siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-700 transition shadow-sm"
                      title="Ouvrir monappliclub.com (nouvel onglet)"
                    >
                      <FiExternalLink /> Voir toutes les apps
                    </a>

                    <a
                      href={donationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 dark:bg-red-600/30 text-white dark:text-white px-4 py-2 text-sm font-semibold border border-gray-200/70 dark:border-slate-700/70 hover:bg-red-700 dark:hover:bg-red-800 transition shadow-sm"
                      title="Faire un don 5€ (nouvel onglet)"
                    >
                      <FiHeart className="text-white" /> Don 5€ (déduction
                      fiscale)
                    </a>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {apps.map((a) => (
                    <span
                      key={a}
                      className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-100 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
                    >
                      {a}
                    </span>
                  ))}
                </div>

                <div className="mt-4 rounded-2xl bg-emerald-50/70 dark:bg-emerald-900/20 border border-emerald-100/70 dark:border-emerald-900/30 p-3">
                  <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                    Merci 🙏
                  </div>
                  <div className="text-xs text-emerald-900/80 dark:text-emerald-100/80 mt-1">
                    Chaque don contribue à développer de nouvelles
                    fonctionnalités et à maintenir ces outils accessibles.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
